import { ApiErrorCode, formatInteger, getApiErrorCode, useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { DutyScheduleResponse, dutySchedulesApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface Options {
  // Runs after the release is confirmed and before it is sent; false aborts it. This is where the
  // pending staffing edits are saved, so a cancelled release saves nothing and a failed save
  // releases nothing.
  onBeforeRelease?: () => Promise<boolean>;
}

export function useDutyScheduleRelease({ onBeforeRelease }: Options = {}) {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const invalidateDutySchedules = () => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.DutySchedules] });
    // The detail page reads its own key, and a Freigabe (release) changes what it may offer.
    void queryClient.invalidateQueries({ queryKey: [QueryKey.DutyScheduleDetail] });
  };

  const releaseMutation = useMutation({
    // Handled locally so a refusal is reported in German rather than as the backend's
    // ProblemDetails.detail, which the global toast would show verbatim.
    mutationFn: (id: string) => dutySchedulesApi.releaseDutySchedule({ id }, withLocalErrorHandling),
    onSuccess: ({ createdAppointmentCount }) => {
      invalidateDutySchedules();
      showSuccess(
        t("dutySchedule.release.success", {
          count: createdAppointmentCount,
          formattedCount: formatInteger(createdAppointmentCount, i18n.language),
        })
      );
    },
    onError: async (error) => {
      const errorCode = await getApiErrorCode(error);
      if (errorCode === ApiErrorCode.DutyScheduleInactiveCounselorsAssigned) {
        showError(t("dutySchedule.release.inactiveCounselors"));
      } else if (errorCode === ApiErrorCode.DutyScheduleAlreadyReleased) {
        invalidateDutySchedules();
        showError(t("dutySchedule.release.alreadyReleased"));
      } else if (errorCode === ApiErrorCode.DutyScheduleNotFound) {
        invalidateDutySchedules();
        showError(t("dutySchedule.release.notFound"));
      } else {
        showError(t("common.requestError"));
      }
    },
  });

  const confirmAndRelease = (dutySchedule: DutyScheduleResponse) => {
    showConfirmationDialog({
      dialogTitleText: t("dutySchedule.release.confirmTitle"),
      dialogText: t("dutySchedule.release.confirmText"),
      agreeText: t("dutySchedule.release.confirm"),
      onClose: (confirmed) => {
        if (!confirmed) {
          return;
        }

        void (async () => {
          if (onBeforeRelease != null && !(await onBeforeRelease())) {
            return;
          }

          releaseMutation.mutate(dutySchedule.id);
        })();
      },
    });
  };

  return { confirmAndRelease, isReleasing: releaseMutation.isPending };
}
